import { useCallback, useEffect, useState } from 'react'
import { usePlaidLink } from 'react-plaid-link'
import * as plaidCommands from '@healthshare/shared/commands/plaid'
import { isExpoApp, runPlaidHostedLink } from '../../lib/plaid/hostedLink'
import { useAppDispatch, useAppSelector } from '../hooks/reduxToolkit'
import { useBackendApi } from '../hooks/useBackendApi'
import { useBackendAuth } from '../hooks/useBackendAuth'
import { useAsyncEffect } from '../hooks/useAsyncEffect'
import { usePlaidLinkFlow } from '../hooks/usePlaidLinkFlow'
import { useSentry } from '../providers/sentry'
import { usePlaidLinkEnabled } from '../providers/featureFlags'
import { Tooltip } from './tooltip'
import {
  PlaidItemModel,
  PlaidItemStatus,
  addPlaidItem,
  removePlaidItem,
  setPlaidItems,
} from '../reduxToolkit/plaidSlice'

function isSyncing(item: PlaidItemModel): boolean {
  // Guard for stale persisted state that predates sync_started_at — see
  // CLAUDE.md "Redux Persist and Stale Schema".
  const started = item.sync_started_at ?? null
  if (!started) return false
  if (!item.last_synced_at) return true
  return started > item.last_synced_at
}

/**
 * "Connected Accounts" section for the settings page. Lists the caller's
 * Plaid items, lets them manually sync, disconnect, or add a new HSA.
 *
 * Always renders. When the user's plan doesn't grant `plaid_hsa_link` (legacy
 * Basic / flag-less org), the "Connect a new account" button is disabled with a
 * "contact us to enable sync" tooltip; pre-signup trial users and users whose
 * organization or paid tier carries the flag can link (see usePlaidLinkEnabled).
 */
export function PlaidConnectedAccountsSection() {
  const canLink = usePlaidLinkEnabled()
  const dispatch = useAppDispatch()
  const api = useBackendApi()
  // Still needed for the Hosted Link bridge below -- see usePlaidLinkFlow.ts.
  const { getAuthHeaders, isAuthenticated } = useBackendAuth()
  const { captureException } = useSentry()
  const membershipId = useAppSelector((s) => s.membership.membershipId)
  const items = useAppSelector((s) => s.plaid.items)
  const loaded = useAppSelector((s) => s.plaid.loaded)

  // Linking a new account is shared with the Expenses draft empty state.
  const { startLink, linking, error: linkError, clearError: clearLinkError, SubscriptionPromptModal } = usePlaidLinkFlow()

  const [actionError, setActionError] = useState<string | null>(null)
  // Update-mode (reconnect) drives its own Plaid Link instance below; the new
  // account flow lives in usePlaidLinkFlow.
  const [reconnectToken, setReconnectToken] = useState<string | null>(null)
  const [busyItemId, setBusyItemId] = useState<string | null>(null)
  const [updateItemId, setUpdateItemId] = useState<string | null>(null)
  // Bumping this tick re-runs the items fetch — used by the in-flight sync poll.
  const [refreshTick, setRefreshTick] = useState(0)

  // The error banner surfaces either this component's actionError or the hook's
  // linkError. They live in separate state, so every Plaid action clears both up
  // front — otherwise a stale banner from a prior failed action (e.g. "Sync
  // now") would linger through an unrelated successful one.
  const resetErrors = () => {
    setActionError(null)
    clearLinkError()
  }

  // useAsyncEffect automatically reports errors to Sentry via useSentry
  const { loading, error: refreshError } = useAsyncEffect(async () => {
    if (!membershipId || !isAuthenticated) return
    const data = await plaidCommands.listItems(api, membershipId)
    dispatch(setPlaidItems(data.items ?? []))
  }, [membershipId, isAuthenticated, api, dispatch, refreshTick])

  const onReconnectSuccess = useCallback(async (itemId: string) => {
    try {
      const data = await plaidCommands.reconnectItem(api, itemId)
      dispatch(addPlaidItem(data.item))
    } catch (e) {
      console.error('Plaid reconnect failed', e)
      captureException(e, { tags: { feature: 'plaid', plaid_op: 'reconnect' } })
      setActionError('Unable to complete reconnection. Please try again.')
    } finally {
      setReconnectToken(null)
      setUpdateItemId(null)
    }
  }, [api, dispatch, captureException])

  const { open, ready } = usePlaidLink({
    token: reconnectToken,
    onSuccess: () => {
      if (updateItemId) {
        void onReconnectSuccess(updateItemId)
      } else {
        console.error('Reconnect mode active but updateItemId is null')
        setActionError('Unable to complete reconnection. Please try again.')
      }
    },
    onExit: () => {
      setReconnectToken(null)
      setUpdateItemId(null)
    },
  })

  useEffect(() => {
    if (reconnectToken && ready) open()
  }, [reconnectToken, ready, open])

  const reconnect = async (itemId: string) => {
    if (!membershipId) return
    resetErrors()
    setUpdateItemId(itemId)
    if (isExpoApp()) {
      try {
        const outcome = await runPlaidHostedLink({ authHeaders: getAuthHeaders(), membershipId, itemId })
        if (outcome.status === 'reconnected') {
          dispatch(addPlaidItem(outcome.item))
        }
      } catch (e) {
        console.error('Plaid hosted reconnect failed', e)
        captureException(e, { tags: { feature: 'plaid', plaid_op: 'hosted-reconnect' } })
        setActionError('Unable to complete reconnection. Please try again.')
      } finally {
        setUpdateItemId(null)
      }
      return
    }
    try {
      const data = await plaidCommands.createLinkToken(api, { membershipId, itemId })
      if (!data.link_token) throw new Error('No link_token')
      setReconnectToken(data.link_token)
    } catch (e) {
      console.error('Plaid reconnect create-link-token failed', e)
      captureException(e, { tags: { feature: 'plaid', plaid_op: 'reconnect-create-link-token' } })
      setActionError('Unable to open the reconnection window.')
      setUpdateItemId(null)
    }
  }

  const disconnect = async (itemId: string) => {
    if (!confirm('Disconnect this account? Transactions already imported will remain.')) return
    resetErrors()
    setBusyItemId(itemId)
    try {
      await plaidCommands.disconnectItem(api, itemId)
      dispatch(removePlaidItem(itemId))
    } catch (e) {
      console.error('Plaid disconnect failed', e)
      captureException(e, { tags: { feature: 'plaid', plaid_op: 'disconnect' } })
      setActionError('Could not disconnect that account.')
    } finally {
      setBusyItemId(null)
    }
  }

  const sync = async (itemId: string) => {
    resetErrors()
    setBusyItemId(itemId)
    try {
      const data = await plaidCommands.syncItem(api, itemId)
      // Stamp sync_started_at immediately so the spinner appears without
      // waiting for the next poll cycle.
      dispatch(addPlaidItem({
        ...items.find((i) => i.id === itemId)!,
        sync_started_at: data.sync_started_at ?? null,
      }))
    } catch (e) {
      console.error('Plaid manual sync failed', e)
      captureException(e, { tags: { feature: 'plaid', plaid_op: 'sync' } })
      setActionError('Could not trigger a sync.')
    } finally {
      setBusyItemId(null)
    }
  }

  // Poll while any item has a sync in flight (sync_started_at > last_synced_at).
  useEffect(() => {
    const anySyncing = items.some((i) => isSyncing(i))
    if (!anySyncing) return
    const id = setInterval(() => setRefreshTick((t) => t + 1), 3000)
    return () => clearInterval(id)
  }, [items])

  return (
    <>
      <hr />
      <h4>Connected Accounts</h4>
      <p className="text-muted">
        Linked accounts import transactions. HSA accounts import everything;
        other accounts import only medical-categorized transactions.
      </p>

      {(refreshError || actionError || linkError) && (
        <div className="alert alert-danger" role="alert">
          {refreshError ? 'Could not load connected accounts.' : (actionError ?? linkError)}
        </div>
      )}

      {loading && !loaded && <div className="loading-bar" />}

      {loaded && items.length === 0 && (
        <p>No accounts connected yet.</p>
      )}

      {items.map((item) => (
        <PlaidItemCard
          key={item.id}
          item={item}
          busy={busyItemId === item.id}
          syncing={isSyncing(item)}
          onDisconnect={() => disconnect(item.id)}
          onSync={() => sync(item.id)}
          onReconnect={() => reconnect(item.id)}
        />
      ))}

      <Tooltip
        tooltip={canLink ? undefined : 'contact us to enable sync'}
        className="d-inline-block mt-2"
      >
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => { resetErrors(); startLink() }}
          disabled={!canLink || !membershipId || linking}
        >
          <i className="material-icons align-middle" style={{ fontSize: 18, marginRight: 6 }}>add</i>
          Connect a new account
        </button>
      </Tooltip>

      <SubscriptionPromptModal />
    </>
  )
}

function PlaidItemCard({
  item,
  busy,
  syncing,
  onSync,
  onDisconnect,
  onReconnect,
}: {
  item: PlaidItemModel
  busy: boolean
  syncing: boolean
  onSync: () => void
  onDisconnect: () => void
  onReconnect: () => void
}) {
  return (
    <div className="card mb-2">
      <div className="card-body">
        {item.status === 'requires_reauth' && (
          <div className="alert alert-warning mb-2 py-2">
            This account needs to be reconnected to continue importing transactions.
            <button
              type="button"
              className="btn btn-warning btn-sm ms-3"
              onClick={onReconnect}
              disabled={busy}
            >
              Reconnect
            </button>
          </div>
        )}
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong>{item.institution_name ?? 'Connected account'}</strong>
            <StatusBadge status={item.status} />
            <div className="text-muted small">
              {item.accounts.map((a) => (
                <span key={a.id} className="me-3">
                  {a.name ?? 'Account'}{a.mask ? ` ••${a.mask}` : ''}
                  {accountPolicyLabel(a)}
                </span>
              ))}
            </div>
            <div className="text-muted small">
              {syncing ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  Syncing…
                </>
              ) : item.last_synced_at ? (
                <>Last synced: {new Date(item.last_synced_at).toLocaleString()}</>
              ) : null}
            </div>
          </div>
          <div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={onSync}
              disabled={busy || syncing}
            >
              Sync now
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={onDisconnect}
              disabled={busy}
            >
              Disconnect
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function accountPolicyLabel(a: { is_hsa_eligible: boolean; import_policy?: 'all_transactions' | 'medical_only' }): string {
  if (a.is_hsa_eligible) return ' (HSA)'
  // Coalesce to 'all_transactions' for rows rehydrated from persisted state
  // that pre-dates this field — see CLAUDE.md "Redux Persist and Stale Schema".
  const policy = a.import_policy ?? 'all_transactions'
  return policy === 'medical_only' ? ' (Medical only)' : ''
}

function StatusBadge({ status }: { status: PlaidItemStatus }) {
  if (status === 'active') {
    return <span className="badge bg-success ms-2">Active</span>
  }
  if (status === 'requires_reauth') {
    return <span className="badge bg-warning text-dark ms-2">Reconnect needed</span>
  }
  return <span className="badge bg-secondary ms-2">Disconnected</span>
}
